/**
 * Authentication service for the Outlook add-in.
 *
 * Strategy:
 * 1. Try Office.auth.getAccessToken() — SSO via the Office host (silent, no popup)
 * 2. If that fails (error codes 13001–13012), fall back to MSAL acquireTokenSilent → acquireTokenPopup
 * 3. Exchange the acquired token for an HTTP-only session cookie via POST /api/auth/addin/token-exchange
 *
 * After initSession() succeeds, all subsequent API calls use the session cookie
 * (credentials: 'include') — no Bearer tokens are stored or transmitted by the client.
 *
 * MSAL session state is stored in sessionStorage only (for MSAL's own cache).
 * We do NOT store access tokens in localStorage or sessionStorage ourselves.
 */
import { PublicClientApplication, InteractionRequiredAuthError, } from "@azure/msal-browser";
import { msalConfig, apiScopes, graphMailSendScopes } from "./msalConfig";
import { setCsrfToken, clearCsrfToken } from "./csrfStore";
const API_BASE = process.env.API_URL || "https://localhost:3001";
// Dev-only: skip Office SSO/MSAL entirely and sign in via the backend's
// POST /api/auth/dev-login instead. Set via addin/.env -> DEV_AUTO_LOGIN=true.
// webpack.config.js refuses to build a production bundle with this set,
// so it cannot ship by accident.
const DEV_AUTO_LOGIN = process.env.DEV_AUTO_LOGIN === "true";
// Log at module load so dev can confirm the bypass is active in the browser console
if (DEV_AUTO_LOGIN) {
    console.info("[Auth] DEV_AUTO_LOGIN=true — will use /api/auth/dev-login bypass");
}
/** Delay helper for retry logic */
function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
// ---------------------------------------------------------------------------
// Singleton MSAL instance
// ---------------------------------------------------------------------------
let msalInstance = null;
// sessionStorage key set right before an interactive redirect so that, after
// Azure AD navigates the task pane back and the add-in reloads, the app knows
// a Graph-token redirect was in flight and can resume the "Send Email" action.
export const PENDING_GRAPH_SEND_KEY = "odyq.pendingGraphSend";
async function getMsalInstance() {
    if (!msalInstance) {
        msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();
        // Process any redirect response FIRST. When the add-in reloads after an
        // acquireTokenRedirect round-trip, this consumes the hash/response and
        // writes the acquired token into the MSAL cache so the follow-up silent
        // acquisition in acquireGraphToken() succeeds without any UI. On a normal
        // (non-redirect) load it resolves null and is a no-op.
        try {
            await msalInstance.handleRedirectPromise();
        }
        catch (err) {
            console.warn("[Auth] handleRedirectPromise failed:", err);
        }
    }
    return msalInstance;
}
/**
 * Whether an interactive Graph-token redirect was in flight before the last
 * reload. The UI checks this on mount to resume the "Send Email" flow.
 * Reading it clears the flag so the resume happens at most once.
 */
export function consumePendingGraphSend() {
    try {
        const pending = sessionStorage.getItem(PENDING_GRAPH_SEND_KEY) === "1";
        if (pending)
            sessionStorage.removeItem(PENDING_GRAPH_SEND_KEY);
        return pending;
    }
    catch (_a) {
        return false;
    }
}
// ---------------------------------------------------------------------------
// Office SSO (preferred — silent, no popup)
// ---------------------------------------------------------------------------
/** Office SSO error codes that indicate fallback to MSAL is needed */
const OFFICE_SSO_FALLBACK_CODES = new Set([
    13001, 13002, 13003, 13004, 13005, 13006, 13007, 13008, 13009, 13010, 13011, 13012,
]);
/**
 * Try Office JS SSO API to get a bootstrap token silently.
 * This only works when the add-in manifest has WebApplicationInfo configured.
 */
async function getOfficeToken() {
    try {
        // Office.auth.getAccessToken returns a token for the add-in's app registration
        const token = await Office.auth.getAccessToken({
            allowSignInPrompt: true,
            allowConsentPrompt: true,
            forMSGraphAccess: false,
        });
        return token || null;
    }
    catch (err) {
        const code = typeof (err === null || err === void 0 ? void 0 : err.code) === "number" ? err.code : parseInt(err === null || err === void 0 ? void 0 : err.code, 10);
        if (!isNaN(code) && OFFICE_SSO_FALLBACK_CODES.has(code)) {
            console.warn("[Auth] Office SSO unavailable (code", code, ") — falling back to MSAL");
        }
        else {
            console.warn("[Auth] Office SSO unavailable:", (err === null || err === void 0 ? void 0 : err.code) || (err === null || err === void 0 ? void 0 : err.message) || err);
        }
        return null;
    }
}
// ---------------------------------------------------------------------------
// MSAL fallback (popup)
// ---------------------------------------------------------------------------
async function acquireTokenMsal() {
    const pca = await getMsalInstance();
    // Try silent first (from cache)
    const accounts = pca.getAllAccounts();
    if (accounts.length > 0) {
        try {
            const result = await pca.acquireTokenSilent({
                scopes: apiScopes,
                account: accounts[0],
            });
            return result;
        }
        catch (err) {
            if (!(err instanceof InteractionRequiredAuthError)) {
                console.warn("[Auth] Silent token acquisition failed:", err);
            }
            // Fall through to popup
        }
    }
    // Interactive popup (MSAL will handle the Azure login page)
    try {
        const result = await pca.acquireTokenPopup({
            scopes: apiScopes,
        });
        return result;
    }
    catch (err) {
        console.error("[Auth] Popup login failed:", err);
        return null;
    }
}
/**
 * Acquire an Azure AD access token (Office SSO → MSAL silent → MSAL popup).
 * Returns null if authentication fails entirely.
 *
 * This is an internal helper used by initSession(). External callers should
 * use initSession() which handles the full token-to-session-cookie flow.
 */
async function acquireAccessToken() {
    // Strategy 1: Office SSO
    const officeToken = await getOfficeToken();
    if (officeToken) {
        // The Office SSO token is a valid AAD token for our app registration.
        // Decode basic claims for user info (non-verified, just for display)
        const claims = parseJwtClaims(officeToken);
        return {
            token: officeToken,
            user: {
                name: claims.name || "",
                email: claims.preferred_username || claims.upn || "",
                oid: claims.oid || "",
                tenantId: claims.tid || "",
            },
        };
    }
    // Strategy 2: MSAL popup fallback
    const msalResult = await acquireTokenMsal();
    if (msalResult && msalResult.accessToken) {
        const account = msalResult.account;
        return {
            token: msalResult.accessToken,
            user: {
                name: (account === null || account === void 0 ? void 0 : account.name) || "",
                email: (account === null || account === void 0 ? void 0 : account.username) || "",
                oid: (account === null || account === void 0 ? void 0 : account.localAccountId) || "",
                tenantId: (account === null || account === void 0 ? void 0 : account.tenantId) || "",
            },
        };
    }
    return null;
}
/**
 * Initialize a server-side session by acquiring a token and exchanging it
 * for an HTTP-only session cookie via POST /api/auth/addin/token-exchange.
 *
 * Flow:
 * 1. Acquire Azure AD token (Office SSO → MSAL silent → MSAL popup)
 * 2. POST the token to the backend token-exchange endpoint with credentials: 'include'
 * 3. The backend validates the token, creates a session, and sets the cookie
 * 4. Store the CSRF token from the response body for subsequent API calls
 *
 * Includes a single retry with 1s delay for transient network failures
 * (e.g. ECS cold start, brief connectivity loss in WebView2).
 *
 * Returns true if session was established successfully, false otherwise.
 */
export async function initSession() {
    if (DEV_AUTO_LOGIN) {
        return devLogin();
    }
    // 1. Acquire Azure AD token
    const authResult = await acquireAccessToken();
    if (!authResult) {
        console.error("[Auth] initSession: failed to acquire access token");
        return false;
    }
    // 2. Exchange for session cookie (with one retry on transient failure)
    const maxAttempts = 2;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const response = await fetch(`${API_BASE}/api/auth/addin/token-exchange`, {
                method: "POST",
                credentials: "include", // critical: sends and receives cookies
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ access_token: authResult.token }),
            });
            if (!response.ok) {
                const detail = await response.text().catch(() => "");
                console.error("[Auth] initSession: token exchange failed:", response.status, detail);
                // Don't retry on 4xx — only retry on 5xx or network errors
                if (response.status >= 400 && response.status < 500) {
                    return false;
                }
                if (attempt < maxAttempts) {
                    console.warn("[Auth] initSession: retrying token exchange after 1s...");
                    await delay(1000);
                    continue;
                }
                return false;
            }
            // 3. Extract and store the CSRF token from the response body
            try {
                const body = await response.json();
                if (body.csrf_token) {
                    setCsrfToken(body.csrf_token);
                }
            }
            catch (_a) {
                // Response may not have a JSON body in all cases — proceed without CSRF token
                console.warn("[Auth] initSession: could not parse CSRF token from response");
            }
            return true;
        }
        catch (err) {
            console.error("[Auth] initSession: network error during token exchange:", err);
            if (attempt < maxAttempts) {
                console.warn("[Auth] initSession: retrying after 1s...");
                await delay(1000);
                continue;
            }
            return false;
        }
    }
    return false;
}
/**
 * Dev-only: sign in via the backend's dev auth bypass instead of Azure AD.
 * Never called unless DEV_AUTO_LOGIN=true, which webpack refuses to set in
 * a production build.
 */
async function devLogin() {
    try {
        const response = await fetch(`${API_BASE}/api/auth/dev-login`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
        });
        if (!response.ok) {
            console.error("[Auth] DEV_AUTO_LOGIN: dev-login failed:", response.status);
            return false;
        }
        // Store CSRF token if returned
        try {
            const body = await response.json();
            if (body.csrf_token) {
                setCsrfToken(body.csrf_token);
            }
        }
        catch (_a) {
            // dev-login may not return JSON — acceptable
        }
        console.warn("[Auth] DEV_AUTO_LOGIN active — signed in via dev bypass, not Azure AD");
        return true;
    }
    catch (err) {
        console.error("[Auth] DEV_AUTO_LOGIN: network error:", err);
        return false;
    }
}
/**
 * Sign out the current user.
 *
 * 1. Revoke the server-side session (best-effort POST to /api/auth/logout)
 * 2. Clear the in-memory CSRF token
 * 3. Clear the MSAL client-side cache via logoutPopup
 */
export async function signOut() {
    // Revoke server session (best-effort — don't block on failure)
    try {
        await fetch(`${API_BASE}/api/auth/logout`, {
            method: "POST",
            credentials: "include",
        });
    }
    catch (_a) {
        // Network failure during logout is non-critical
    }
    // Clear client-side CSRF state
    clearCsrfToken();
    // Clear MSAL cache
    const pca = await getMsalInstance();
    const accounts = pca.getAllAccounts();
    if (accounts.length > 0) {
        await pca.logoutPopup({ account: accounts[0] });
    }
}
/**
 * Check if the server-side session is still active by calling GET /api/auth/me.
 *
 * Returns true if the session cookie is valid and the backend confirms the session.
 * Falls back to checking MSAL cache if the network call fails.
 */
export async function isSessionActive() {
    try {
        const response = await fetch(`${API_BASE}/api/auth/me`, {
            method: "GET",
            credentials: "include",
        });
        return response.ok;
    }
    catch (_a) {
        // Network failure — fall back to local MSAL state as a heuristic
        return isSignedIn();
    }
}
/**
 * Check if user has a cached MSAL account locally.
 * Note: This does NOT guarantee the server session is valid.
 * Prefer isSessionActive() for authoritative session checks.
 */
export async function isSignedIn() {
    try {
        const pca = await getMsalInstance();
        return pca.getAllAccounts().length > 0;
    }
    catch (_a) {
        return false;
    }
}
/**
 * Acquire a delegated Microsoft Graph access token for the signed-in user.
 *
 * Used by the "Send Email" flow to obtain a `Mail.Send`-scoped token that the
 * backend forwards to Graph's `replyAll` endpoint. Strategy:
 *   1. MSAL silent (from the account cached at sign-in) — no UI.
 *   2. MSAL interactive popup — handles first-time `Mail.Send` consent or an
 *      expired/absent cached token.
 *
 * NOTE on this deployment: the add-in manifests intentionally omit
 * `WebApplicationInfo` (Exchange sideloading rejects it), so Office SSO
 * (`Office.auth.getAccessToken`) is NOT available here — sign-in runs entirely
 * through MSAL, which means an MSAL account normally exists after login and the
 * silent path above usually succeeds once `Mail.Send` has been consented.
 *
 * The token is returned to the caller for immediate forwarding to the API and
 * is NOT stored by this module.
 *
 * @param scopes - Graph scopes to request. Defaults to `Mail.Send`.
 * @returns The Graph access token string.
 * @throws Error with a user-facing message describing why acquisition failed.
 */
export async function acquireGraphToken(scopes = graphMailSendScopes) {
    const pca = await getMsalInstance();
    const accounts = pca.getAllAccounts();
    const account = accounts[0];
    // 1. Silent — reuses the account cached at sign-in when consent already exists.
    if (account) {
        try {
            const result = await pca.acquireTokenSilent({ scopes, account });
            if (result === null || result === void 0 ? void 0 : result.accessToken)
                return result.accessToken;
        }
        catch (err) {
            // Non-interaction errors are unexpected; log them. Interaction-required
            // (no consent yet / token expired) is normal — fall through to the popup.
            if (!(err instanceof InteractionRequiredAuthError)) {
                console.warn("[Auth] Silent Graph token acquisition failed:", err);
            }
        }
    }
    // 2. Interactive popup — first-time Mail.Send consent or no usable cache.
    // Pass the known account + loginHint so Azure AD skips the account picker
    // and consents against the already-signed-in user.
    const interactiveRequest = Object.assign(Object.assign({ scopes }, (account ? { account, loginHint: account.username } : {})), { prompt: "consent" });
    try {
        const result = await pca.acquireTokenPopup(interactiveRequest);
        if (result === null || result === void 0 ? void 0 : result.accessToken)
            return result.accessToken;
        throw new Error("No access token returned from Azure AD.");
    }
    catch (err) {
        console.error("[Auth] Graph token popup failed:", err);
        const msg = (err === null || err === void 0 ? void 0 : err.errorMessage) || (err === null || err === void 0 ? void 0 : err.message) || String(err);
        // If the popup was blocked/could-not-open in this Outlook client, fall back
        // to a full-page redirect. This navigates the task pane to Azure AD and
        // back; the add-in reloads, getMsalInstance() consumes the response via
        // handleRedirectPromise(), and the resumed flow (see consumePendingGraphSend)
        // retries the silent acquisition — which now succeeds from the cache.
        if (isPopupBlockedError(msg)) {
            console.warn("[Auth] Popup unavailable — falling back to redirect for Graph consent");
            try {
                sessionStorage.setItem(PENDING_GRAPH_SEND_KEY, "1");
            }
            catch (_a) {
                /* sessionStorage may be unavailable in some partitioned contexts */
            }
            // This call navigates away and does not return; the promise never
            // resolves because the page unloads.
            await pca.acquireTokenRedirect(interactiveRequest);
            // Unreachable in practice, but keeps the type checker happy.
            throw new Error("Redirecting to sign in…");
        }
        if (/consent|AADSTS65001|invalid_scope|AADSTS70011/i.test(msg)) {
            throw new Error("Permission to send mail (Mail.Send) hasn't been granted for this app. Ask an administrator to consent, then try again.");
        }
        throw new Error("Could not get permission to send mail. Please sign in again.");
    }
}
/** True when the MSAL error indicates the popup could not open or was dismissed. */
function isPopupBlockedError(msg) {
    return /popup_window_error|popup.*block|empty_window_error|user_cancelled|user_canceled|BrowserAuthError/i.test(msg);
}
/**
 * Resume a Graph-token acquisition after an interactive redirect round-trip.
 *
 * Called on app mount when consumePendingGraphSend() is true. By this point
 * handleRedirectPromise() (run inside getMsalInstance) has placed the freshly
 * consented token in the MSAL cache, so a plain silent acquisition returns it
 * with no UI. Returns the token, or null if the cache still has nothing usable
 * (e.g. the user cancelled consent at the Azure AD page).
 */
export async function resumeGraphTokenAfterRedirect(scopes = graphMailSendScopes) {
    var _a;
    const pca = await getMsalInstance();
    const account = pca.getAllAccounts()[0];
    if (!account)
        return null;
    try {
        const result = await pca.acquireTokenSilent({ scopes, account });
        return (_a = result === null || result === void 0 ? void 0 : result.accessToken) !== null && _a !== void 0 ? _a : null;
    }
    catch (err) {
        console.warn("[Auth] resumeGraphTokenAfterRedirect: silent acquisition failed:", err);
        return null;
    }
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function parseJwtClaims(token) {
    try {
        const payload = token.split(".")[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    }
    catch (_a) {
        return {};
    }
}
